import { Col, Row } from 'antd'
import { GetServerSideProps, NextPage } from 'next'
import React, { useCallback, useState } from 'react'
import { SEOHead } from '~/components/business/SEOHead'
import { GlobalRequest } from '~/components/global-requset'
import { Container } from '~/components/ui/Container'
import { GET_COLLECTION_DETAIL } from '~/graphqls/gqls'
import {
  ArtworkSortEnum,
  ArtworkStatusEnum,
  getCollectionDetail,
  getCollectionDetailVariables,
  getCollectionDetail_marketingModule_collection,
  getCollectionDetail_marketingModule_collection_productList_edges_node,
  RareFlag,
  SeriesType,
  SortType,
} from '~/graphqls/types'
import { Layout } from '~/layouts/default'
import { getGraphData, serverSideCatch } from '~/utils'
import { SingleProduct } from '~/components/business/SingleProduct'
import { MyEmpty } from '~/components/ui/MyEmpty'
import { InfiniteScroll } from '~/components/business/InfiniteScroll'
import { PAGE_SIZE } from '~/configs'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import styled from 'styled-components'
import { MyImage } from '~/components/ui/MyImage'
import Link from 'next/link'
import { MySteryBoxProduct } from '~/components/pages/collection/MysteryBoxProduct'
import UserBadge from '~/components/business/UserBadge'
import { ReadMore } from '~/components/business/ReadMore'
import { PageSort, SortItemProps } from '~/components/business/PageSort'
import { FitlerItemProps, PageFilter } from '~/components/business/PageFilter'
import _ from 'lodash'
import { PresaleProduct } from '~/components/pages/collection/PresaleProduct'
import { VipIcon } from '~/components/icon/Vip'

const StyledCollectionDetailHeader = styled.div`
  border-radius: 16px;
  margin-bottom: 32px;
  position: relative;
  height: 428px;
  .collection-bg {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    border-radius: 16px;
  }
  .collection-content {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 270px;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.8) 100%
    );
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
    padding: 32px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: end;
  }
  .collection-title {
    display: flex;
    font-size: 32px;
    line-height: 40px;
    font-weight: 600;
    color: ${props => props.theme.light3};
    margin-bottom: 24px;
    align-items: center;
  }
  .collection-count {
    color: ${props => props.theme.dark2};
    background: ${props => props.theme.light2};
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 16px;
    line-height: 24px;
    margin-left: 12px;
    font-weight: 400;
    > span {
      font-weight: 600;
    }
  }
  .collection-creator {
    position: relative;
    display: inline-flex;
    margin-bottom: 24px;
    align-items: center;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 180px;
    padding: 4px 16px 4px 4px;
    backdrop-filter: blur(32px);
  }
  .creator-avatar-wrap {
    position: relative;
    height: 44px;
    width: 44px;
  }

  .creator-avatar {
    border-radius: 50%;
    height: 100%;
    width: 100%;
  }
  .creator-name {
    margin-left: 12px;
    font-size: 24px;
    line-height: 32px;
    color: ${props => props.theme.light3};
    font-weight: 600;
  }

  .collection-desc {
    font-size: 14px;
    line-height: 20px;
    color: ${props => props.theme.light1};
  }
`

const sortData: SortItemProps[] = [
  {
    key: ArtworkSortEnum.SERIES_SORT,
    name: '默认',
  },
  {
    key: ArtworkSortEnum.CREATE_TIME,
    name: '最新',
  },
  {
    key: ArtworkSortEnum.HOT,
    name: '最热',
  },
  {
    key: ArtworkSortEnum.PRICE,
    name: '价格',
    sort: true,
  },
]

const CollectionDetail: NextPage<{
  collection: getCollectionDetail_marketingModule_collection
}> = ({ collection }) => {
  const [loading, setLoading] = useState(false)
  console.log(collection)
  const getCollectionDetail = useImperativeQuery<
    getCollectionDetail,
    getCollectionDetailVariables
  >(GET_COLLECTION_DETAIL)

  const [pageInfo, setPageInfo] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
  })

  const [productList, setProductList] = useState<
    getCollectionDetail_marketingModule_collection_productList_edges_node[]
  >(getGraphData(collection?.productList?.edges || []))

  const [hasNextPage, setHasNextPage] = useState(
    collection?.productList?.pageInfo?.hasNextPage || false
  )

  const [sort, setSort] = useState({
    sortField: ArtworkSortEnum.SERIES_SORT,
    sortType: SortType.DESC,
  })

  const filterData: FitlerItemProps[] = [
    {
      key: '',
      name: '全部',
    },
    ...(collection?.rareList?.map((item: any) => {
      return {
        key: item,
        name: item,
      }
    }) || []),
  ]

  const isMysteryBox = collection?.type === SeriesType.BLIND_BOX
  const isPresale = collection?.type === SeriesType.CROWDFUND

  const isMysteryBoxProduct = useCallback(
    status => {
      return isMysteryBox && status === ArtworkStatusEnum.NOT_VISIBLE
    },
    [isMysteryBox]
  )

  const isPresaleProduct = useCallback(
    status => {
      return isPresale && status === ArtworkStatusEnum.NOT_VISIBLE
    },
    [isPresale]
  )

  const onSortChange = (item: SortItemProps, sortType: SortType) => {
    setPageInfo({
      page: 1,
      pageSize: PAGE_SIZE,
    })

    setSort(sort => ({
      ...sort,
      sortField: item.key as ArtworkSortEnum,
      sortType,
    }))

    getCollectionDetail({
      id: collection.id,
      first: PAGE_SIZE,
      offset: 0,
      sort: {
        ...sort,
        sortField: item.key as ArtworkSortEnum,
        sortType,
      },
    }).then(data => {
      setProductList(
        getGraphData(
          data.data.marketingModule?.collection?.productList?.edges || []
        )
      )

      setHasNextPage(
        data?.data?.marketingModule?.collection?.productList?.pageInfo
          ?.hasNextPage || false
      )
    })
  }

  const onFilterChange = (item: FitlerItemProps) => {
    setPageInfo({
      page: 1,
      pageSize: PAGE_SIZE,
    })

    const newSort =
      item.key === ''
        ? _.omit(sort, 'rare')
        : {
            ...sort,
            rare: item.key as RareFlag,
          }

    setSort(newSort)

    getCollectionDetail({
      id: collection.id,
      first: PAGE_SIZE,
      offset: 0,
      sort: newSort,
    }).then(data => {
      setProductList(
        getGraphData(
          data.data.marketingModule?.collection?.productList?.edges || []
        )
      )

      setHasNextPage(
        data?.data?.marketingModule?.collection?.productList?.pageInfo
          ?.hasNextPage || false
      )
    })
  }

  const onScroll = useCallback(() => {
    setLoading(true)

    getCollectionDetail({
      id: collection.id,
      first: pageInfo.pageSize,
      offset: pageInfo.pageSize * pageInfo.page,
      sort,
    })
      .then(data => {
        setProductList(list => {
          /* 备注：不去重是因为盲盒id都为null */
          return [
            ...list,
            ...getGraphData(
              data.data.marketingModule?.collection?.productList?.edges || []
            ),
          ]
        })

        setHasNextPage(
          data?.data?.marketingModule?.collection?.productList?.pageInfo
            ?.hasNextPage || false
        )

        setPageInfo(page => {
          return {
            pageSize: page.pageSize,
            page: page.page + 1,
          }
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [collection, getCollectionDetail, pageInfo, sort])

  return (
    <Layout>
      <SEOHead title={collection.name} />
      <Container>
        <StyledCollectionDetailHeader>
          <MyImage
            tag="v1200"
            className="collection-bg"
            src={collection?.image}
          />
          <div className="collection-content">
            <div className="collection-title">
              {collection.name}
              <span className="collection-count">
                <span>{collection.artworkCount}</span> 作品
              </span>
            </div>
            <Link href={`/user/${collection.creator?.id}`}>
              <a>
                <div className="collection-creator">
                  <a>
                    <div className="creator-avatar-wrap">
                      <MyImage
                        tag="v400"
                        className="creator-avatar"
                        src={collection?.creator?.avatar}
                      />
                      <VipIcon
                        userRole={collection?.creator?.userRoleInfo}
                        className="h-4 -right-[4px]  max-w-[24px]"
                      />
                    </div>
                  </a>

                  <div className="creator-name">
                    {collection?.creator?.name}
                    <UserBadge
                      type={collection?.creator?.userType}
                      className="ml-1 !w-[18px] !h-[18px] !align-middle"
                    />
                  </div>
                </div>
              </a>
            </Link>
            <ReadMore
              className="collection-desc"
              text={collection.description || ''}
              title="作品集介绍"
              limit={120}
            />
          </div>
        </StyledCollectionDetailHeader>
        <PageSort
          data={sortData}
          onChange={onSortChange}
          extra={
            (isMysteryBox || isPresale) && (
              <PageFilter data={filterData} onChange={onFilterChange} />
            )
          }
        />

        {productList?.length ? (
          <>
            <Row gutter={32}>
              {productList?.map((item, index) => (
                <Col span={6} key={index}>
                  {isMysteryBoxProduct(item.status) ? (
                    <MySteryBoxProduct data={collection} />
                  ) : isPresaleProduct(item.status) ? (
                    <PresaleProduct data={collection} />
                  ) : (
                    <SingleProduct showCreator={false} data={item} showRank />
                  )}
                </Col>
              ))}
            </Row>
            <InfiniteScroll
              onScroll={onScroll}
              hasNextPage={hasNextPage}
              loading={loading}
            />
          </>
        ) : (
          <MyEmpty />
        )}
      </Container>
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async context => {
  return await serverSideCatch(async () => {
    const { initialState, client } = await GlobalRequest(context)
    const collectionId = Number(context.params?.id)

    const collectionRes = await client.query<
      getCollectionDetail,
      getCollectionDetailVariables
    >({
      query: GET_COLLECTION_DETAIL,
      variables: {
        id: collectionId,
        first: PAGE_SIZE,
        offset: 0,
      },
      fetchPolicy: 'no-cache',
    })
    console.log(collectionRes)
    return {
      props: {
        initialState,
        collection: collectionRes.data.marketingModule?.collection,
      },
    }
  })
}

export default CollectionDetail
